import Player from "@vimeo/player";
import type { FC } from "react";
import { useEffect, useRef, useState } from "react";
import { Badge } from "react-bootstrap";

import { VIDEO_INIT_TIME_RETURN_SECONDS, VIDEO_PROGRESS_RESET_THRESHOLD } from "const";
import IconCheck from "images/icons/check--blue.svg";

import IconPlayButton from "components/Icons/IconPlayButton/IconPlayButton";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { getUserEmail, setIsSoundMuted } from "store/slices/user";
import { getProgress, updateTime } from "utils/common/video";
import { useImageLoaded } from "utils/hooks/useImageLoaded";
import { useVisibleAssetUrl } from "utils/hooks/useVisibleAssetUrl";

interface Props {
  videoId: string;
  previewImage: string | null;
  title?: string | null;
  onFinished?: (videoId: string) => void;
  /** Skryje spodní řádek (název + štítek „Přehráno") — kreslí ho místo toho volající sám,
   * viz masterclass, kde by jinak duplikoval vlastní nadpis a štítek „Zhlédnuto". [TD-327] */
  hideMeta?: boolean;
  /** Klíč pro ukládání progresu (getProgress/updateTime), výchozí `videoId`. Masterclass
   * posílá ID dokumentu, ne Vimeo ID — dvě různá videa mohou sdílet stejný Vimeo zdroj, a bez
   * odděleného klíče by zhlédnutí jednoho označilo za zhlédnuté i to druhé. [TD-327] */
  progressKey?: string;
  /** Ztmavení + play tlačítko 60 px přes náhled, dokud se nenačte iframe (Figma masterclass
   * 13207:42376). Výchozí `false`, ať VideoTips a Tutoring zůstanou beze změny. [TD-327 C-1] */
  playOverlay?: boolean;
}

const VideoWithStatus: FC<Props> = ({
  videoId,
  previewImage,
  title,
  onFinished,
  hideMeta = false,
  progressKey,
  playOverlay = false,
}) => {
  const videoSrc = `https://player.vimeo.com/video/${videoId}?badge=0&autopause=0&player_id=0&app_id=58479`;
  const defaultImagePath = "/images/video-placeholder.png";
  const storageKey = progressKey ?? videoId;

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const playerRef = useRef<Player | null>(null);
  const duration = useRef<number | null>(null);
  const isViewedRef = useRef<boolean>(false); // duplicated because of the closure issue in onTimeUpdate
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isViewed, setIsViewed] = useState(false);
  const userEmail = useAppSelector(getUserEmail);
  const dispatch = useAppDispatch();

  // AIDEV-NOTE: `useVisibleAssetUrl`, ne `useAssetUrlResolver` jako jinde. `/videotipy` kreslí
  // 31 dlaždic bez virtualizace a resolvování při mountu znamenalo 31 requestů na Storage API
  // v jedné vlně a hned za nimi 31 náhledů po ~400 kB (~12 MB). Cenou je, že rozbitá cesta ve
  // Storage teď nechá dlaždici prázdnou místo výchozího placeholderu — hook neodliší „ještě
  // neresolvováno" od „selhalo", stejný kompromis jako u mřížky knih. [TD-363, TD-362]
  const { url: previewUrl, ref: previewRef, isVisible, isSettled } = useVisibleAssetUrl(previewImage);

  // Selhaný resolve (smazaný soubor ve Storage) končí na výchozím placeholderu, ne na
  // prázdné dlaždici — `isSettled` je jediné, co ho odliší od „ještě se stahuje".
  const placeholderImageUrl = previewImage ? (previewUrl ?? (isSettled ? defaultImagePath : null)) : defaultImagePath;

  // Ghost drží až do `onLoad`, ne do vyresolvování URL: resolve je jen první polovina
  // čekání, po něm se teprve táhne bitmapa. Týž vzor jako `BookCover`. [TD-349]
  const {
    isLoaded: isPlaceholderLoaded,
    ref: placeholderRef,
    onLoad: onPlaceholderLoad,
    onError: onPlaceholderError,
  } = useImageLoaded(placeholderImageUrl);

  // AIDEV-NOTE: Podmínkou je `isVisible`, ne jen „nemám URL". Mřížka /videotipy má přes
  // dvacet dlaždic pod ohybem a shimmer je nekonečná animace — rozsvítit ho i tam, kde se
  // ještě nezačalo nic stahovat, by vrátilo přesně tu zátěž, kvůli které se náhled odkládá.
  // Mřížka knih to řeší jinak, `content-visibility: auto` na dlaždici; tady se to nehodí,
  // protože `.video-wrapper` nese i přehrávané video ve `SolutionModal`. [TD-363]
  const isGhostVisible = isVisible && !isPlaceholderLoaded;

  useEffect(() => {
    if (!userEmail) return;

    const initSavedData = getProgress(storageKey, userEmail);
    if (!initSavedData) return;

    setIsViewed(initSavedData.isViewed ?? false);
    isViewedRef.current = initSavedData.isViewed ?? false;
  }, [storageKey, userEmail]);

  useEffect(() => {
    if (!iframeRef.current) return;

    const player = new Player(iframeRef.current);
    playerRef.current = player;
    initPlayer(player);

    const onTimeUpdate = (data: { seconds: number }) => {
      if (!duration.current) return;

      const currentTime = data.seconds;
      const progressPercent = (currentTime / duration.current) * 100;

      // AIDEV-NOTE: Ref, ne stav `isViewed` — ten je tu zachycený z renderu, kdy efekt běžel
      // (závislost jen `isVideoLoaded`), a zůstane `false`. Každý timeupdate za prahem (~4×/s)
      // by pak znovu volal onFinished, na masterclass mutaci `masterclassVideoViewed`. [TD-452]
      if (!isViewedRef.current && progressPercent >= VIDEO_PROGRESS_RESET_THRESHOLD) {
        setIsViewed(true);
        onFinished?.(videoId);
        isViewedRef.current = true;
      }

      if (!userEmail) return;

      updateTime(storageKey, currentTime, progressPercent, isViewedRef.current, userEmail);
    };

    player.on("timeupdate", onTimeUpdate);
    player.on("play", () => {
      toggleBgSound("off");
    });
    player.on("pause", () => {
      toggleBgSound("on");
    });
    player.on("ended", () => {
      toggleBgSound("on");
      try {
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(() => {});
        } else if ((document as any).webkitFullscreenElement) {
          (document as any).webkitExitFullscreen?.();
        }
      } catch (err) {
        // ignore
      }
    });

    return () => {
      player.off("timeupdate", onTimeUpdate);
      player.off("play");
      player.off("pause");
      player.off("ended");

      toggleBgSound("on");

      (async () => {
        await destroyPlayer(player);
      })().catch(() => {});
      playerRef.current = null;
    };
  }, [isVideoLoaded]);

  const initPlayer = (player: Player) => {
    setLastWatchedTime(player);
    setVideoDuration(player).catch(() => {});
    player
      .ready()
      .then(async () => {
        await player.play();
        await player.setVolume(1);
        await player.setMuted(false);
      })
      .catch(() => {});
  };

  const setLastWatchedTime = (player: Player) => {
    if (!userEmail) return;

    const savedVideoData = getProgress(storageKey, userEmail);
    if (!savedVideoData) return;

    const timeBackByTwoSeconds = Math.max(0, savedVideoData.time - VIDEO_INIT_TIME_RETURN_SECONDS);

    setIsViewed(savedVideoData.isViewed ?? false);
    player.setCurrentTime(timeBackByTwoSeconds).catch(err => {
      console.warn("Failed to set start time", err);
    });

    if (savedVideoData.isViewed && onFinished) {
      onFinished(videoId);
    }
  };

  const setVideoDuration = async (player: Player) => {
    duration.current = await player.getDuration();
  };

  const toggleBgSound = (status: "on" | "off") => {
    dispatch(setIsSoundMuted(status === "off"));
  };

  const destroyPlayer = async (player: Player) => {
    try {
      await player.destroy();
    } catch (err) {
      console.warn("Failed to destroy Vimeo player", err);
    }
  };

  return (
    <>
      {/* Ref sleduje obal, ne tlačítko s podkladem — obal existuje i než URL dorazí. */}
      <div ref={previewRef} className={`video-wrapper ${isViewed ? "video-wrapper--viewed" : ""}`}>
        {isVideoLoaded ? (
          <iframe ref={iframeRef} src={videoSrc} allow="autoplay; fullscreen; picture-in-picture" />
        ) : (
          <button
            className={isGhostVisible ? "img-ghost" : undefined}
            onClick={() => {
              setIsVideoLoaded(true);
            }}
          >
            {placeholderImageUrl === null ? null : (
              <img
                ref={placeholderRef}
                onLoad={onPlaceholderLoad}
                onError={onPlaceholderError}
                src={placeholderImageUrl}
                alt=""
              />
            )}
            {playOverlay ? (
              <>
                <span className="video-wrapper__overlay" aria-hidden="true" />
                <IconPlayButton className="video-wrapper__play" aria-hidden="true" />
              </>
            ) : null}
          </button>
        )}
      </div>
      {hideMeta ? null : (
        <>
          <div className="video-wrapper__title">{title}</div>
          <Badge
            bg={"green"}
            className={`badge-circular bg-success video-wrapper__icon ${isViewed ? "" : "video-wrapper__icon--hidden"}`}
          >
            <IconCheck className={"me-4px"} />
            <span>Přehráno</span>
          </Badge>
        </>
      )}
    </>
  );
};

export default VideoWithStatus;
